"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import AnimateOnScroll from "./AnimateOnScroll";
import CustomButton from "./CustomButton";
import { ThemeName, BRANCH_GRADIENT } from "./theme";
import { reactNodeToText } from "@/lib/utils";
import { SITE_URL } from "@/lib/metadata";
import { trackBookAppointmentClicked } from "@/lib/analytics";

type BranchTheme = Exclude<ThemeName, "white">;

interface BranchItem {
  label: string;
  href: string;
}

interface BranchGroup {
  heading: string;
  theme: BranchTheme;
  items: BranchItem[];
}

const defaultGroups: BranchGroup[] = [
  {
    heading: "Counselling and Therapy",
    theme: "purple",
    items: [
      { label: "Anger Management Counselling", href: "/anger-management" },
      { label: "Relationship Counselling", href: "/relationships" },
      { label: "Depression & Anxiety", href: "/depression-anxiety" },
      { label: "Grief and Loss", href: "/grief-loss" },
      { label: "Career Counselling", href: "/career-counselling" },
    ],
  },
  {
    heading: "Life Coach and Personal Development",
    theme: "yellow",
    items: [
      { label: "Self-Esteem & Confidence", href: "/self-esteem-confidence" },
      { label: "Time Management & Focus", href: "/time-management" },
      { label: "Focus & Productivity", href: "/focus-productivity" },
      { label: "Changing Habits", href: "/changing-habits" },
      { label: "Public Speaking", href: "/public-speaking" },
    ],
  },
  {
    heading: "Allied Health Service",
    theme: "red",
    items: [
      { label: "Occupational Therapy", href: "/occupational-therapy" },
      { label: "Psychology", href: "/psychology" },
    ],
  },
];

interface BranchesProps {
  heading?: React.ReactNode;
  description?: string;
  ctaHref?: string;
  ctaLabel?: string;
  groups?: BranchGroup[];
  theme?: BranchTheme;
}

const Branches = ({
  heading = (
    <>
      View All <br /> Our Services
    </>
  ),
  description = "No matter where you are in your life struggle, seeking help is a powerful sign of strength. With guidance, advice, and understanding, you achieve a deeper awareness of yourself, discover coping mechanisms, and work toward achieving your goals for better wellbeing.",
  ctaHref = "/booking",
  ctaLabel = "Book an Appointment",
  groups = defaultGroups,
  theme = "blue",
}: BranchesProps) => {
  const servicesListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: reactNodeToText(heading),
    itemListElement: groups
      .flatMap((group) => group.items)
      .map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: new URL(item.href, SITE_URL).href,
        name: item.label,
      })),
  };

  return (
    <section
      className="container bg-[#F7FAFF]"
      aria-labelledby="branches-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(servicesListSchema).replace(/</g, "\\u003c"),
        }}
      />
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-10 2xl:gap-20">
        {/* Left */}
        <AnimateOnScroll className="flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
          <h2 id="branches-heading" className="heading text-black">
            {heading}
          </h2>
          <p className="font-onest text-[14px] font-normal leading-[1.7] text-black/60 lg:text-[15px]">
            {description}
          </p>
          <div>
            <CustomButton
              label={ctaLabel}
              href={ctaHref}
              variant="primary"
              withIcon
              theme={theme}
              onClick={() => trackBookAppointmentClicked({ location: "branches" })}
            />
          </div>
        </AnimateOnScroll>

        {/* Right: groups */}
        <div className="flex flex-col gap-8">
          {groups.map((group) => (
            <div
              key={group.heading}
              style={
                {
                  "--branch-gradient": BRANCH_GRADIENT[group.theme],
                } as React.CSSProperties
              }
            >
              <h3 className="mb-1 font-crimsonText text-[20px] font-semibold text-black sm:text-[22px]">
                {group.heading}
              </h3>
              <div className="flex flex-col">
                {group.items.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="group flex items-center justify-between px-3 py-[14px] transition-all duration-300 hover:[background:var(--branch-gradient)]"
                    style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.20)" }}
                    aria-label={item.label}
                  >
                    <span className="font-onest text-[15px] font-normal leading-snug text-black/80 lg:text-[16px]">
                      {item.label}
                    </span>
                    <ArrowRight
                      size={16}
                      className="shrink-0 text-black/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Branches;
